// ---------------------------------------------------------------------------
// The product list the POS draws from, whichever side of a connection it is on.
//
// Online it is the ordinary paged query, unchanged from Phase 2 - the server
// filters, and the till shows what it is given.
//
// Offline it is the snapshot in IndexedDB, filtered here in the browser. That
// is the one place in this system where filtering happens client-side, and it
// is unavoidable: there is nothing to ask.
//
// The snapshot deliberately carries no cost prices, so a cashier selling
// offline sees exactly what they would see online - a price to charge and
// nothing about what the shop paid.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import { useProducts, type ProductFilters } from '../api/catalog.api';
import { captureCatalogue, loadCachedCatalogue } from '../lib/syncEngine';
import { isEffectivelyOffline, useOfflineStore } from '../stores/offlineStore';
import type { CachedCatalogue } from '../lib/offlineDb';
import type { Category, Product } from '../types';

interface CatalogueResult {
  products: Product[];
  categories: Category[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  /// True when the list came from the device rather than the server.
  fromCache: boolean;
  /// When the snapshot was taken. Null when it came from the server.
  capturedAt: string | null;
  /// True when the till is offline AND has no snapshot, which is the one
  /// state in which it genuinely cannot sell.
  unavailable: boolean;
}

/// Match a product against the search box, the way the server does.
function matches(product: Product, search: string): boolean {
  if (!search) return true;

  const needle = search.trim().toLowerCase();
  return (
    product.nameEn.toLowerCase().includes(needle) ||
    product.nameAr.includes(needle) ||
    product.sku.toLowerCase().includes(needle) ||
    (product.barcode ?? '').toLowerCase().includes(needle)
  );
}

export function usePosCatalogue(
  filters: ProductFilters,
  onlineCategories: Category[] | undefined,
): CatalogueResult {
  const browserOnline = useOfflineStore((state) => state.browserOnline);
  const serverReachable = useOfflineStore((state) => state.serverReachable);
  const offline = isEffectivelyOffline({ browserOnline, serverReachable });

  const [cached, setCached] = useState<CachedCatalogue | null>(null);
  const [loadingCache, setLoadingCache] = useState(true);

  // The snapshot is read once on mount, so going offline mid-shift finds it
  // already in memory rather than waiting on IndexedDB with a queue forming.
  useEffect(() => {
    let alive = true;

    void loadCachedCatalogue().then((catalogue) => {
      if (!alive) return;
      setCached(catalogue);
      setLoadingCache(false);
    });

    return () => {
      alive = false;
    };
  }, []);

  // Refreshed whenever the till is online and looking at the POS. Cheap, and
  // it means the snapshot is never much older than the last time somebody
  // opened the till with a connection.
  useEffect(() => {
    if (offline) return;

    void captureCatalogue().then((saved) => {
      if (saved) void loadCachedCatalogue().then(setCached);
    });
  }, [offline]);

  const online = useProducts(filters, !offline);

  const offlineProducts = useMemo(() => {
    if (!cached) return [];

    const all = cached.products as Product[];

    return all
      .filter((product) => {
        if (filters.categoryId && product.category.id !== filters.categoryId) return false;
        return matches(product, filters.search ?? '');
      })
      // The SAME order the server serves: the shop's arrangement of that
      // shelf. Without this the offline grid would fall back to whatever
      // order the snapshot happened to be written in, and the tile a cashier
      // reaches for by muscle memory would move the moment the connection
      // dropped - exactly when they can least afford a surprise.
      .sort(
        (left, right) =>
          left.sortOrder - right.sortOrder || left.nameEn.localeCompare(right.nameEn),
      );
  }, [cached, filters.categoryId, filters.search]);

  if (!offline) {
    return {
      products: online.data?.products ?? [],
      categories: onlineCategories ?? [],
      isLoading: online.isLoading,
      error: online.error,
      refetch: () => void online.refetch(),
      fromCache: false,
      capturedAt: null,
      unavailable: false,
    };
  }

  return {
    products: offlineProducts,
    categories: (cached?.categories as Category[]) ?? onlineCategories ?? [],
    isLoading: loadingCache,
    error: null,
    refetch: () => void loadCachedCatalogue().then(setCached),
    fromCache: true,
    capturedAt: cached?.capturedAt ?? null,
    unavailable: !loadingCache && cached === null,
  };
}
